import store from "@/store"

class Device {
  constructor() {
    this.power = false
    this.properties = null
    this.numpad = false
    this.defaultVolume = 0
    this.maxVolume = 0
  }
  async _init() {
    _$.vue.$on("power", e => this._powerStateHandler(e.detail))
    this.power = await this.getPowerState()
    if (!this.power) throw "Unable to get device power state"

    await this._registerKeys()
    await this._registerKeyMap()

    this.properties = await this._getPropertiesWithRetry()
    if (!this.properties) throw "Unable to get device properties"

    this.numpad = __._device.numpad
    _$.vue.$once("config.init", () => this.setTimeZoneLG())
    return true
  }

  async _getPropertiesWithRetry(attempts = 40, delay = 3000) {
    let error = null

    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        const properties = await __._device.properties()
        if (properties) return properties
      } catch (err) {
        error = err
      }

      _$.log(`Device properties are not ready. Retrying ${attempt}/${attempts}...`, false)
      if (attempt < attempts) await _$.sleep(delay)
    }

    if (error) _$.error(error)
    return false
  }

  get(property = null) {
    if (!store.state.device) return null

    if (property) return store.state.device[property]
    else return store.state.device
  }
  config(property = null) {
    if (!store.state.config) return null
    if (property) {
      let _property = store.state.config[property]
      if (["true", "false"].indexOf(_property) >= 0) _property = _property === "true"
      return _property
    } else return store.state.config
  }
  async _assign(exist = true) {
    if (exist) return true

    const device = this.get()
    if (!device) store.commit("splash", false)

    const location = await _$.vue.$interact.prompt(
      _$.vue.$t("Device Not Found"),
      _$.vue.$t("Please specify the location you want to assign the Device to."),
      "left",
      [{ type: "input", id: "room", fluid: true, label: "Location Number" }]
    )
    if (!location) return this._assign(exist)

    const result = await store.dispatch("setDevice", location.room.value)
    if (result) {
      if (!device) store.commit("splash", true)
      return true
    }

    await _$.vue.$interact.alert(_$.vue.$t("Location not found!"))
    return this._assign(exist)
  }
  async assign() {
    if (!this.get()) return false

    const device = _$.clone(this.get())
    const properties = _$.clone(this.properties)
    const location = await _$.vue.$interact.prompt(
      _$.vue.$t("Change Location"),
      _$.vue.$t("Please specify the location you want to assign the Device to."),
      "left",
      [{ type: "input", id: "room", label: "Location Number", value: device.location }]
    )
    if (!location || device.location == location.room.value) return false
    properties.location = location.room.value
    properties.id = device.id

    const result = await __.api.update.device(properties).then(data => data && __.platform.syncData("device"))
    if (result) return true

    await _$.vue.$interact.alert(_$.vue.$t("Location not found!"))
    return this.assign()
  }
  async update() {
    if (!this.get()) return false
    const device = _$.clone(this.get())
    const properties = _$.clone(this.properties)
    properties.location = device.location
    properties.id = device.id

    return __.api.update.device(properties)
  }
  async baseUrl() {
    return window.location.origin
  }
  async setName(name) {
    return __._device.setName(name).catch(err => _$.error(err))
  }
  async getName() {
    return __._device.getName().catch(err => _$.error(err))
  }
  async getTimeZone() {
    return __._device.getTimeZone().catch(err => _$.error(err))
  }
  async setTimeZone(timeZoneId) {
    return __._device.setTimeZone(timeZoneId).catch(err => _$.error(err))
  }
  async setTimeZoneLG() {
    try {
      const raw = __.device.config("lgtimezone.lgtimezone")
      if (!raw) return
      const config = typeof raw === "string" ? JSON.parse(raw) : raw
      if (config.enable !== 1 && config.enable !== "1") return
      if (__.device.get("type") !== "hcap") return
      return __._device.setTimeZone(config).catch(err => _$.error(err))
    } catch (e) {
      _$.error("setTimeZone failed:", e)
    }
  }

  /* Register Methods */
  async _registerKeys() {
    return __._device.registerKeys().catch(err => _$.error(err))
  }
  async _registerKeyMap() {
    return __._device
      .getKeyMap()
      .then(data => {
        _$.nav._keyMapping = data
        return true
      })
      .finally(() => {
        window.P_KEY = {}
        _$.nav._keyMapping.map(key_ => {
          if (key_.length === 3) window.P_KEY[`${key_[1]}_${key_[2]}`] = key_[0]
          else window.P_KEY[key_[1]] = key_[0]
        })
      })
      .catch(err => _$.error(err))
  }

  /* Volume Management */
  async volume(volume) {
    if (typeof volume !== "number") return __._device.volume().catch(err => _$.error(err))

    if (volume < 0) volume = 0
    else if (volume > this.maxVolume) volume = this.maxVolume
    else if (volume > 100) volume = 100

    return __._device.volume(volume).catch(err => _$.error(err))
  }
  async mute(value) {
    if ([0, 1, true, false].indexOf(value) >= 0) return __._device.mute(value).catch(err => _$.error(err))
    else return __._device.mute().catch(err => _$.error(err))
  }
  async setMaxVolume() {
    const useStbVolume = __.device.get("type") === "playmoregtv" ? await this._shouldUseStbVolumeProfile() : this._isLegacyStbType()
    const configName = useStbVolume ? "maxVolumeSTB.maxVolumeSTB" : "maxVolume.maxVolume"
    if (!this.maxVolume) this.maxVolume = parseInt(__.device.config(configName))
    return __._device.maxVolume().catch(err => _$.error(err))
  }
  async setDefaultVolume() {
    const useStbVolume = __.device.get("type") === "playmoregtv" ? await this._shouldUseStbVolumeProfile() : this._isLegacyStbType()
    const configName = useStbVolume ? "defVolumeSTB.defVolumeSTB" : "defVolume.defVolume"
    if (!this.defaultVolume) this.defaultVolume = parseInt(__.device.config(configName))

    const currentVolume = await this.volume()
    if (this.defaultVolume === currentVolume) return true

    return this.volume(this.defaultVolume)
  }

  /* Power Management */
  isPowerOn() {
    return this.power === "on"
  }
  async getPowerState() {
    return __._device.getPowerState().catch(err => _$.error(err))
  }
  async _powerStateHandler(data) {
    if (!data) return false
    this.power = data
    this._dispatchPowerState()
    return true
  }
  async _dispatchPowerState() {
    if (this.power === "on") _$.vue.$emit("powerOn")
    else if (this.power === "standby") _$.vue.$emit("standBy")
    return true
  }
  async powerOn() {
    return __._device.powerOn().catch(err => _$.error(err))
  }
  async standBy() {
    return __._device.standBy().catch(err => _$.error(err))
  }
  async reboot() {
    return __._device.reboot().catch(err => _$.error(err))
  }
  async clearData() {
    return __._device.clearData().catch(err => _$.error(err))
  }

  /* Soft AP */
  async getSoftAP() {
    return __._device.getSoftAP().catch(err => _$.error(err))
  }
  async enableSoftAP() {
    const ssid = this.generateSoftAPSSID()
    const password = this.generateSoftAPPassword()

    return __._device.enableSoftAP(ssid, password).catch(err => _$.error(err))
  }
  async disableSoftAP() {
    return __._device.disableSoftAP().catch(err => _$.error(err))
  }
  async isSoftAPEnabled() {
    return __._device.isSoftAPEnabled().catch(err => _$.error(err))
  }
  generateSoftAPSSID() {
    return __.device.get("name")
  }
  generateSoftAPPassword() {
    return __.device
      .get("mac")
      .replace(/:/g, "")
      .slice(4)
  }
  softAPState() {
    if (!__.device.config("connect_behaviour.connect_behaviour")) return 0
    return parseInt(__.device.config("connect_behaviour.connect_behaviour"))
  }
  async _shouldUseStbVolumeProfile() {
    if (typeof __._device.getVolumeProfile === "function") {
      const profile = await Promise.resolve(__._device.getVolumeProfile(this.properties)).catch(() => null)
      if (profile === "stb") return true
      if (profile === "tv") return false
    }
    return this._isLegacyStbType()
  }

  _isLegacyStbType() {
    const type = __.device.get("type")
    if (type === "playmore") return true
    if (type === "playmoreatv") return true
    return false
  }
}

export default new Device()
